import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Brain, Cloud, Server, Shield, Database, Network, 
  Monitor, ArrowRight, CheckCircle, Target, Award,
  Search, Filter, BarChart, Book, MessageSquare, Clock
} from 'lucide-react';

type Course = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  skills: string[];
  duration: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  status: 'available' | 'coming-soon';
  progress?: number;
  certification?: string;
  path: string;
};

const TrainingPortal = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const courses: Course[] = [
    {
      id: 'az-104',
      title: 'Azure Administrator',
      subtitle: 'AZ-104 Certification',
      description: 'Learn to manage Azure subscriptions, secure identities, administer infrastructure, configure virtual networking, and more.',
      icon: <Cloud className="h-6 w-6 text-accent" />,
      skills: ['Cloud Computing', 'Infrastructure', 'Security'],
      duration: '40 hours',
      level: 'intermediate',
      status: 'available',
      certification: 'Microsoft Certified: Azure Administrator Associate',
      path: '/training/azure-admin-module-1'
    },
    {
      id: 'az-204',
      title: 'Azure Developer',
      subtitle: 'AZ-204 Certification',
      description: 'Learn to develop solutions using Azure services, including storage, security, compute, and more.',
      icon: <Server className="h-6 w-6 text-accent" />,
      skills: ['Development', 'Cloud Services', 'APIs'],
      duration: '45 hours',
      level: 'intermediate',
      status: 'coming-soon',
      certification: 'Microsoft Certified: Azure Developer Associate',
      path: '/training/azure-developer'
    },
    {
      id: 'az-305',
      title: 'Azure Solutions Architect',
      subtitle: 'AZ-305 Certification',
      description: 'Design solutions that run on Azure, including compute, network, storage, and security.',
      icon: <Network className="h-6 w-6 text-accent" />,
      skills: ['Architecture', 'Solutions Design', 'Best Practices'],
      duration: '50 hours',
      level: 'advanced',
      status: 'coming-soon',
      certification: 'Microsoft Certified: Azure Solutions Architect Expert',
      path: '/training/azure-architect'
    }
  ];

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLevel = selectedLevel === 'all' || course.level === selectedLevel;
    return matchesSearch && matchesLevel;
  });

  const getLevelBadgeColor = (level: string) => {
    switch (level) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-foreground sm:text-5xl">
            Training Portal
          </h1>
          <p className="mt-4 text-xl text-foreground/80">
            Accede a formación especializada y certificaciones profesionales
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-foreground/50" />
          </div>
          <div className="flex space-x-4">
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="px-4 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="all">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
            <button className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30">
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>
        </div>

        {/* Course Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {filteredCourses.map((course) => (
            <div
              key={course.id}
              className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
            >
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center">
                  {course.icon}
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-foreground">{course.title}</h3>
                  <p className="text-sm text-foreground/70">{course.subtitle}</p>
                  {course.status === 'coming-soon' && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mt-1">
                      Coming Soon
                    </span>
                  )}
                </div>
              </div>

              <p className="text-foreground/80 mb-4">{course.description}</p>

              <div className="flex flex-wrap gap-2 mb-4">
                {course.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent"
                  >
                    {skill}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-foreground/50 mr-1" />
                    <span className="text-sm text-foreground/70">{course.duration}</span>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLevelBadgeColor(course.level)}`}>
                    {course.level.charAt(0).toUpperCase() + course.level.slice(1)}
                  </span>
                </div>
                {course.certification && (
                  <div className="flex items-center">
                    <Award className="h-4 w-4 text-accent mr-1" />
                    <span className="text-sm text-foreground/70">Certification</span>
                  </div>
                )}
              </div>

              {course.status === 'available' ? (
                <Link
                  to={course.path}
                  className="inline-flex items-center text-accent hover:text-accent/90"
                >
                  Start Course
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              ) : (
                <button
                  disabled
                  className="inline-flex items-center text-foreground/50 cursor-not-allowed"
                >
                  Coming Soon
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-foreground mb-8">
            Por qué elegir nuestro Training Portal
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="dark-card p-6">
              <Brain className="h-8 w-8 text-accent mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Mentoría IA 24/7</h3>
              <p className="text-foreground/80">
                Accede a un mentor de IA disponible 24/7 para resolver dudas y recibir feedback personalizado.
              </p>
            </div>

            <div className="dark-card p-6">
              <Target className="h-8 w-8 text-accent mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Aprendizaje Práctico</h3>
              <p className="text-foreground/80">
                Aprende con ejercicios prácticos y simulaciones del mundo real que refuerzan tus conocimientos.
              </p>
            </div>

            <div className="dark-card p-6">
              <Award className="h-8 w-8 text-accent mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Certificaciones Oficiales</h3>
              <p className="text-foreground/80">
                Prepárate para obtener certificaciones oficiales reconocidas en la industria.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <Link
            to="/login"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-accent hover:bg-accent/90 transition-all duration-300"
          >
            Comenzar mi formación
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TrainingPortal;